import { sectionRenderer } from '@theme/section-renderer';

class ResourcesLibrary extends HTMLElement {
  connectedCallback() {
    this.aborted = false;
    this.loadingPages = false;
    this.cards = Array.from(this.querySelectorAll('[data-resource-card]'));
    this.checkboxes = Array.from(
      this.querySelectorAll('[data-resource-filter] .checkbox__input, input[data-resource-filter]')
    );
    this.table = this.querySelector('[data-resources-table]');
    this.grid = this.querySelector('[data-resources-grid]');
    this.emptyState = this.querySelector('[data-resources-empty]');
    this.status = this.querySelector('[data-resources-status]');
    this.sentinel = this.querySelector('[data-resources-sentinel]');
    this.mobileToggle = this.querySelector('[data-resources-mobile-toggle]');
    this.filtersList = this.querySelector('[data-resources-filters]');
    this.pageSize = Number(this.dataset.pageSize) || 16;
    this.visibleCount = this.pageSize;

    this.onFilterChange = this.onFilterChange.bind(this);
    this.onMobileToggle = this.onMobileToggle.bind(this);

    this.checkboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', this.onFilterChange);
    });

    if (this.mobileToggle && this.filtersList) {
      this.mobileToggle.addEventListener('click', this.onMobileToggle);
    }

    this.applyTypeFromUrl();
    this.setupObserver();
    this.applyFilters();
    this.loadRemainingPages();
  }

  /**
   * Pre-select Resource Type filters from ?type= query params.
   * Supports stored categories (Spec Sheets) and menu aliases (Specifications).
   * Examples: /pages/resources?type=Spec%20Sheets | ?type=specifications
   */
  applyTypeFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const typeParams = params
      .getAll('type')
      .flatMap((value) => value.split(','))
      .map((value) => value.trim())
      .filter(Boolean);

    if (typeParams.length === 0) return;

    const aliases = {
      specifications: 'spec sheets',
      specification: 'spec sheets',
      specs: 'spec sheets',
      'spec sheets': 'spec sheets',
      'submittal sheets': 'submittal sheets',
      'submittal sheet': 'submittal sheets',
      submittals: 'submittal sheets',
      submittal: 'submittal sheets',
      'ies files': 'ies',
      'ies-files': 'ies',
      'ies file': 'ies',
      ies: 'ies',
      'technical documents': 'technical documents',
      technical: 'technical documents',
      certifications: 'certificates',
      certification: 'certificates',
      certificates: 'certificates',
      certificate: 'certificates',
      'marketing materials': 'marketing materials',
      marketing: 'marketing materials',
      brochure: 'marketing materials',
      'hojas de especificaciones': 'spec sheets',
      'hojas de presentacion': 'submittal sheets',
      'hojas de presentación': 'submittal sheets',
      'documentos tecnicos': 'technical documents',
      'documentos técnicos': 'technical documents',
      certificados: 'certificates',
      'materiales de marketing': 'marketing materials',
    };

    const targets = new Set(
      typeParams.map((value) => {
        const normalized = value.toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
        return aliases[normalized] || normalized;
      })
    );

    this.checkboxes.forEach((checkbox) => {
      const value = checkbox.value.toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
      if (targets.has(value)) {
        checkbox.checked = true;
      }
    });
  }

  disconnectedCallback() {
    this.aborted = true;

    this.checkboxes.forEach((checkbox) => {
      checkbox.removeEventListener('change', this.onFilterChange);
    });

    if (this.mobileToggle) {
      this.mobileToggle.removeEventListener('click', this.onMobileToggle);
    }

    if (this.observer) {
      this.observer.disconnect();
    }
  }

  setupObserver() {
    if (!this.sentinel || !('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.loadMore();
          }
        });
      },
      { rootMargin: '200px 0px' }
    );

    this.observer.observe(this.sentinel);
  }

  onMobileToggle() {
    const expanded = this.mobileToggle.getAttribute('aria-expanded') === 'true';
    this.mobileToggle.setAttribute('aria-expanded', String(!expanded));
    this.filtersList.classList.toggle('is-open', !expanded);
  }

  onFilterChange() {
    this.visibleCount = this.pageSize;
    this.applyFilters();
  }

  getActiveTypes() {
    return this.checkboxes.filter((checkbox) => checkbox.checked).map((checkbox) => checkbox.value);
  }

  getMatchingCards() {
    const activeTypes = this.getActiveTypes();

    if (activeTypes.length === 0) {
      return this.cards;
    }

    return this.cards.filter((card) => activeTypes.includes(card.dataset.resourceType));
  }

  cardKey(card) {
    const handle = card.dataset.resourceHandle || '';
    const title = card.dataset.resourceTitle || '';
    return `${handle}|${card.dataset.resourceType}|${title}`;
  }

  applyFilters() {
    this.syncVisibility();
    this.fillViewport();
  }

  syncVisibility() {
    const matchingCards = this.getMatchingCards();
    const visibleCards = matchingCards.slice(0, this.visibleCount);

    this.cards.forEach((card) => {
      card.hidden = true;
    });

    visibleCards.forEach((card) => {
      card.hidden = false;
    });

    const hasMatches = matchingCards.length > 0;
    const hasMore = matchingCards.length > this.visibleCount || this.loadingPages;

    if (this.emptyState) {
      this.emptyState.hidden = hasMatches || this.loadingPages;
    }

    if (this.status) {
      this.status.hidden = !hasMore;
    }

    if (this.table) {
      this.table.hidden = !hasMatches && !this.loadingPages;
    } else if (this.grid) {
      this.grid.hidden = !hasMatches && !this.loadingPages;
    }
  }

  fillViewport() {
    if (!this.sentinel) return;

    let guard = 0;
    while (guard < 40) {
      const matchingCards = this.getMatchingCards();
      if (this.visibleCount >= matchingCards.length) break;

      const top = this.sentinel.getBoundingClientRect().top;
      if (top >= window.innerHeight + 200) break;

      this.visibleCount += this.pageSize;
      this.syncVisibility();
      guard += 1;
    }
  }

  loadMore() {
    const matchingCards = this.getMatchingCards();

    if (this.visibleCount >= matchingCards.length) {
      if (!this.loadingPages && this.status) this.status.hidden = true;
      return;
    }

    this.visibleCount += this.pageSize;
    this.applyFilters();
  }

  updateFilterCounts(hideEmpty) {
    this.checkboxes.forEach((checkbox) => {
      const count = this.cards.filter((card) => card.dataset.resourceType === checkbox.value).length;
      const countEl = checkbox
        .closest('[data-resource-filter]')
        ?.querySelector('[data-resources-filter-count]');

      if (countEl) {
        countEl.textContent = `(${count})`;
      }

      const item = checkbox.closest('[data-resources-filter-item]');
      if (item && hideEmpty) {
        item.hidden = count === 0;
      }
    });
  }

  appendCardsFromHtml(html, prepend) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const incoming = Array.from(doc.querySelectorAll('[data-resource-card]'));
    const existing = new Set(this.cards.map((card) => this.cardKey(card)));
    const fragment = document.createDocumentFragment();
    let added = 0;

    incoming.forEach((card) => {
      if (existing.has(this.cardKey(card))) return;

      const imported = document.importNode(card, true);
      imported.hidden = true;
      existing.add(this.cardKey(imported));
      fragment.appendChild(imported);
      added += 1;
    });

    if (!this.grid || added === 0) return;

    if (prepend && this.grid.firstChild) {
      this.grid.insertBefore(fragment, this.grid.firstChild);
    } else {
      this.grid.appendChild(fragment);
    }

    this.cards = Array.from(this.querySelectorAll('[data-resource-card]'));
    this.updateFilterCounts(false);
    this.applyFilters();

    if (this.sentinel && this.sentinel.getBoundingClientRect().top < window.innerHeight + 200) {
      this.loadMore();
    }
  }

  async loadRemainingPages() {
    const totalPages = Number(this.dataset.paginatePages) || 1;
    const currentPage = Number(this.dataset.paginatePage) || 1;
    const sectionId = this.dataset.sectionId;
    const pageParam = this.dataset.paginateParam || 'page';

    if (!sectionId || totalPages <= 1 || window.Shopify?.designMode) return;

    this.loadingPages = true;
    this.applyFilters();

    try {
      for (let page = 1; page <= totalPages; page += 1) {
        if (this.aborted) return;
        if (page === currentPage) continue;

        const url = new URL(window.location.href);
        url.searchParams.delete('type');
        url.searchParams.set(pageParam, String(page));

        const html = await sectionRenderer.getSectionHTML(sectionId, true, url);
        if (this.aborted) return;
        this.appendCardsFromHtml(html, page < currentPage);
      }
    } catch (error) {
      console.error(error);
    } finally {
      if (this.aborted) return;
      this.loadingPages = false;
      this.updateFilterCounts(true);
      this.applyFilters();
    }
  }
}

if (!customElements.get('resources-library')) {
  customElements.define('resources-library', ResourcesLibrary);
}
